import { DoneOutline, ErrorSharp } from "@mui/icons-material";
import { Alert, Box, Collapse, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from "@mui/material";
import { saveAs } from "file-saver";
import { useState } from "react";
import * as XLSX from "xlsx";
import { LoadingComp } from "../../../../shared/components/loading/Loading";
import { useToast } from "../../../../shared/hooks";
import { handleValidate } from "../../hooks/handleValidate";
import { useAnalysisActions } from "../../hooks/useAnalysisActions";
import { useExcelImport } from "../../hooks/useExcelImport";
import { headers, REQUIRED_HEADERS, RowData } from "../../types";
import { ToolbarToolsAnalysis } from "../ToolbarToolsAnalysis";

export const AnalysisTab = () => {
	const toast = useToast();
	const [data, setData] = useState<RowData[]>([]);
	const [missingFields, setMissingFields] = useState<string[]>([]);
	const [loading, setLoading] = useState(false);
	const [payloads, setPayloads] = useState<any[]>([]);
	const [logs, setLogs] = useState(false);
	const [fileInfo, setFileInfo] = useState<{ name: string; rowCount: number; date: string } | null>(null);

	const { handleAnalyzeClick, handleSaveClick } = useAnalysisActions({
		data,
		setData,
		setPayloads,
		setLoading,
		logs,
	});

	let currentFileName = "";

	const { handleFile } = useExcelImport({
		requiredHeaders: REQUIRED_HEADERS,
		limit: 100,
		onStart: () => {
			setLoading(true);
			setData([]);
			setPayloads([]);
			setMissingFields([]);
			setFileInfo(null);
		},
		onFinish: () => setLoading(false),
		onSuccess: (processed) => {
			if (processed.length >= 100) {
				toast.present({
					type: "warning",
					messageOrContent: "Importação limitada a 100 linhas. Linhas adicionais foram ignoradas.",
				});
			}
			const indexed = processed.map((row, index) => ({
				...row,
				RowNumber: index,
			}));
			const validated = handleValidate(indexed);
			setData(validated);
			setFileInfo({
				name: currentFileName,
				rowCount: validated.length,
				date: new Date().toLocaleString(),
			});
		},
		onError: (missing) => setMissingFields(missing),
	});

	const handleDownloadModelCSV = () => {
		const csv = headers.join(";") + "\n";
		saveAs(new Blob([csv], { type: "text/csv;charset=utf-8;" }), "modelo_importacao.csv");
	};

	const handleExportToExcel = () => {
		if (!data.length) return;

		// Remove campos internos como RowNumber para exportação
		const exportData = data.map(({ RowNumber, ...rest }) => rest);
		const worksheet = XLSX.utils.json_to_sheet(exportData);
		const workbook = XLSX.utils.book_new();
		XLSX.utils.book_append_sheet(workbook, worksheet, "Análise");
		XLSX.writeFile(workbook, "resultado_analise.xlsx");
	};

	const visibleColumns: (keyof RowData)[] = ["RowNumber", "cpf", "nome", "email", "telefone", "iniciogarantia", "fimgarantia", "status", "mensagem"];

	return (
		<Box component={Paper} display="flex" flexDirection="column" padding={2} gap={2} mx={1}>
			{loading && <LoadingComp isopen={loading} />}

			<Typography variant="h6">Análise de Dados</Typography>

			{fileInfo && (
				<Typography variant="body2" color="text.secondary">
					Arquivo: <strong>{fileInfo.name}</strong> | Linhas: <strong>{fileInfo.rowCount}</strong> | Importado em: <strong>{fileInfo.date}</strong>
				</Typography>
			)}

			<ToolbarToolsAnalysis
				logs={logs}
				onToggleLogs={setLogs}
				onFileImport={(file) => {
					currentFileName = file.name;
					handleFile(file);
				}}
				onAnalyze={handleAnalyzeClick}
				onDownloadModel={handleDownloadModelCSV}
				onExportResult={handleExportToExcel}
				onSave={() => handleSaveClick(payloads)}
			/>

			<Collapse in={missingFields.length > 0}>
				<Alert severity="error">
					Campos obrigatórios ausentes: <strong>{missingFields.join(", ")}</strong>
				</Alert>
			</Collapse>

			{data.length ? (
				<TableContainer component={Paper} sx={{ maxHeight: 550 }}>
					<Table stickyHeader size="small">
						<TableHead>
							<TableRow>
								{visibleColumns.map((key) => (
									<TableCell key={key}>{String(key).toUpperCase()}</TableCell>
								))}
							</TableRow>
						</TableHead>
						<TableBody>
							{data.map((row) => (
								<TableRow key={row.RowNumber} hover>
									{visibleColumns.map((key) => (
										<TableCell key={key}>
											{key === "status" ? (
												<Box display="flex" alignItems="center" gap={1}>
													{row[key] === "inválido" ? (
														<ErrorSharp sx={{ color: "#ff3022" }} />
													) : (
														<DoneOutline sx={{ color: "#3ae940" }} />
													)}
													<Typography fontWeight="bold" color={row[key] === "inválido" ? "#ff3022" : "#3ae940"}>
														{typeof row[key] === "object" ? JSON.stringify(row[key]) : String(row[key] ?? "")}
													</Typography>
												</Box>
											) : key === "RowNumber" ? (
												<Typography fontWeight="bold" color="text.secondary">
													#{(Number(row[key]) + 1).toString().padStart(3, "0")}
												</Typography>
											) : (
												String(row[key] ?? "")
											)}
										</TableCell>
									))}
								</TableRow>
							))}
						</TableBody>
					</Table>
				</TableContainer>
			) : (
				<Typography variant="body2" color="text.secondary">
					Nenhum dado importado ainda.
				</Typography>
			)}
		</Box>
	);
};
